import {
  buildCanvasPath,
  buildLibraryFilePath,
  PROJECTS_AREA_SEGMENT,
  PROJECTS_LIBRARY_SEGMENT,
  PROJECTS_TASKS_SEGMENT,
} from "@hightouch/lib/llm/asset-comments/paths";

import { CreationSource } from "@hightouch/lib/llm/shared";

import { type Noun, noun } from "src/utils/noun";

/**
 * Marks a session as an AMP chat. The AMP agent panel lists only sessions whose
 * context carries it, and the backend loads the AMP chat's skills for them
 * (`isAmpSession`).
 */
export const AMP_CHAT_SCOPE = { amp: "true" } as const;

const PANEL_CHAT_SOURCES = [CreationSource.Agents] as const;
const PANEL_AND_HOME_CHAT_SOURCES = [
  CreationSource.Agents,
  CreationSource.Home,
] as const;

/** Creation sources of the chats the AMP agent panel lists. Home chats are AMP chats only under AMP v2, and keep the unbilled Home source. */
export const ampChatListCreationSources = (
  isAmpV2: boolean,
): readonly CreationSource[] =>
  isAmpV2 ? PANEL_AND_HOME_CHAT_SOURCES : PANEL_CHAT_SOURCES;

// Every user-facing name in the project-management area, so renaming it is an edit here; its URL segments are in @hightouch/lib/llm/asset-comments/paths.
// Code and backend identifiers keep saying project and task whatever the product calls them.

export const PROJECTS_TERMS = {
  /** The area's name, as page titles show it. */
  area: "Projects",
  project: noun("project", "projects"),
  task: noun("task", "tasks"),
  subtask: noun("subtask", "subtasks"),
  tag: noun("tag", "tags"),
  workflow: noun("workflow", "workflows"),
  group: noun("group", "groups"),
  chat: noun("chat", "chats"),
  /** One linked piece of a project's work, such as a brief, an audience, or a file. */
  workItem: noun("work item", "work items"),
} as const;

/** What a failed request says when the cause is on our side. */
export const SYSTEM_ERROR_MESSAGE =
  "Something went wrong on our end. Try again, or contact support if it keeps happening.";

/** What a list or view says when it fails to load, beside a retry where the view offers one. */
export const LOAD_ERROR_MESSAGE = SYSTEM_ERROR_MESSAGE;

/** "1 task", "3 tasks". */
export function countOf(count: number, term: Noun): string {
  return `${count} ${count === 1 ? term.singular : term.plural}`;
}

// Mention emails link into the area too, so the segments they use live with the comment link builders.
export const PROJECTS_BASE_PATH = `/${PROJECTS_AREA_SEGMENT}`;
export const PROJECTS_INBOX_SEGMENT = "inbox";
export const PROJECTS_EMAIL_TEMPLATES_SEGMENT = "email-templates";
export const PROJECTS_EMAIL_TEMPLATE_DRAFTS_SEGMENT = "email-template-drafts";
export const PROJECTS_AD_TEMPLATES_SEGMENT = "ad-templates";
// Not "settings": the workspace's own settings live there.
export const PROJECTS_SETTINGS_SEGMENT = "project-settings";

/** The area's top-level URL segments, each of which opens the area's own nav. */
export const PROJECTS_AREA_SEGMENTS = [
  PROJECTS_AREA_SEGMENT,
  PROJECTS_TASKS_SEGMENT,
  PROJECTS_LIBRARY_SEGMENT,
  PROJECTS_EMAIL_TEMPLATES_SEGMENT,
  PROJECTS_EMAIL_TEMPLATE_DRAFTS_SEGMENT,
  PROJECTS_AD_TEMPLATES_SEGMENT,
  PROJECTS_SETTINGS_SEGMENT,
] as const;

/** A task view's URL params that filter it to these people, over its remembered filters. */
export const TASK_PEOPLE_PARAMS = {
  assigneeIds: "assignee",
} as const;

export const PROJECTS_PATHS = {
  tasks: `/${PROJECTS_TASKS_SEGMENT}`,
  /** The tasks page filtered to one assignee, whatever filters the viewer last left there. */
  tasksAssignedTo: (userId: string) =>
    `/${PROJECTS_TASKS_SEGMENT}?${TASK_PEOPLE_PARAMS.assigneeIds}=${encodeURIComponent(userId)}`,
  task: (taskId: string) => `/${PROJECTS_TASKS_SEGMENT}/${taskId}`,
  projects: PROJECTS_BASE_PATH,
  project: (projectId: string) => `${PROJECTS_BASE_PATH}/${projectId}`,
  /** A task seen from within its project, which keeps the project open in the area's navigation. */
  projectTask: (projectId: string, taskId: string) =>
    `${PROJECTS_BASE_PATH}/${projectId}/${PROJECTS_TASKS_SEGMENT}/${taskId}`,
  settings: `/${PROJECTS_SETTINGS_SEGMENT}`,
  library: `/${PROJECTS_LIBRARY_SEGMENT}`,
  /** The Library's New view: a chat to start work, the viewer's recent files, and ideas. */
  libraryNew: `/${PROJECTS_LIBRARY_SEGMENT}/new`,
  file: buildLibraryFilePath,
  emailTemplate: (emailId: string) =>
    `/${PROJECTS_EMAIL_TEMPLATES_SEGMENT}/${emailId}`,
  emailTemplateDraft: (emailId: string) =>
    `/${PROJECTS_EMAIL_TEMPLATE_DRAFTS_SEGMENT}/${emailId}`,
  adTemplate: (templateId: string) =>
    `/${PROJECTS_AD_TEMPLATES_SEGMENT}/${templateId}`,
  canvas: buildCanvasPath,
} as const;

/** Where a Library file opens, as the Library opens it: a canvas on its own page, any other file in the Library's focus view. */
export function libraryFilePath(file: {
  id: string;
  canvasId: string | null;
}): string {
  return file.canvasId
    ? PROJECTS_PATHS.canvas(file.canvasId)
    : PROJECTS_PATHS.file(file.id);
}

/** What a task's owners and assignees are called, and what a task without any says. */
export const TASK_PEOPLE_LABELS = {
  owner: { label: "Owner", emptyLabel: "No owner" },
  assignee: { label: "Assignees", emptyLabel: "Unassigned" },
} as const satisfies Record<string, { label: string; emptyLabel: string }>;
